import { EventType, useGetEventPhotoByEventHandleQuery } from '@graphql/generated';

type UseEventPhoto = Maybe<{ id?: string; url?: string }>;
/**
 * To fetch the best photo URL for a given event handle.
 *
 * @param {string} [eventHandle] - The unique event handle.
 * @param {boolean} [shouldSkipCall] - Whether to skip fetching the photo.
 */
export const useEventPhoto = (eventHandle?: string, shouldSkipCall?: boolean) => {
  const { data, ...rest } = useGetEventPhotoByEventHandleQuery({
    variables: { website: eventHandle! },
    skip: !eventHandle || shouldSkipCall,
    batchMode: 'fast',
  });

  const eventPhoto: UseEventPhoto = extractEventPhoto(data?.eventByName);

  return { eventPhoto, ...rest };
};

interface SupportedHeaderPhotoLayoutSchema {
  photos?: ReadonlyArray<{ id: string; url: string }>;
}

type Event = {
  photo?: { id: string; url: string } | null | undefined;
  photoV2Url?: string | null;
  info: {
    eventType: EventType;
  };
  eventDesign?: Maybe<{
    activeWebsiteHeaderPresentationLayout?: Maybe<{
      dataJSON?: SupportedHeaderPhotoLayoutSchema;
    }>;
  }>;
};

export const extractEventPhoto = <T extends Event>(event?: Maybe<T>): { id: string | undefined; url: string | undefined } | undefined => {
  // Priority 1: photoV2Url (if exist, consider it as eventPhoto)
  if (event?.photoV2Url) {
    return { id: undefined, url: event.photoV2Url };
  }
  // Priority 2: photo (legacy photo object)
  const eventPhoto = event?.photo;
  if (eventPhoto) {
    return { id: eventPhoto.id, url: eventPhoto.url };
  }
  // Priority 3: header layout photo (fallback to design system)
  const eventDesignPhoto = (event?.eventDesign?.activeWebsiteHeaderPresentationLayout?.dataJSON as SupportedHeaderPhotoLayoutSchema)?.photos?.[0];
  if (eventDesignPhoto) {
    return { id: eventDesignPhoto.id, url: eventDesignPhoto.url };
  }
  return;
};
